import { useId, type ReactNode } from "react"

import { cn } from "@/lib/utils"

import { navIconActiveGlowClassName } from "./appearance"
import type { NavIconVariant } from "./types"

type Props = {
  variant: NavIconVariant
  className?: string
  children: ReactNode
}

export function NavIconPaint({ variant, className, children }: Props) {
  const uid = useId().replace(/:/g, "")
  const fillId = `nav-icon-fill-${uid}`
  const strokeId = `nav-icon-stroke-${uid}`

  if (variant === "inactive") {
    return (
      <span
        className={cn(
          "inline-flex size-6 shrink-0 items-center justify-center overflow-visible text-white/50",
          className
        )}
      >
        {children}
      </span>
    )
  }

  return (
    <span
      className={cn(
        "relative inline-flex size-6 shrink-0 items-center justify-center overflow-visible",
        navIconActiveGlowClassName,
        className
      )}
      style={{
        ["--nav-icon-fill" as string]: `url(#${fillId})`,
        ["--nav-icon-stroke" as string]: `url(#${strokeId})`
      }}
    >
      <svg
        aria-hidden
        className="pointer-events-none absolute size-0 overflow-visible"
        width="0"
        height="0"
      >
        <defs>
          <radialGradient id={fillId} cx="50%" cy="115%" r="95%">
            <stop offset="0" stopColor="white" />
            <stop
              offset="0.38"
              stopColor="var(--color-purple-200)"
              stopOpacity="0.5"
            />
            <stop offset="1" stopColor="var(--color-purple-500)" />
          </radialGradient>
          <linearGradient id={strokeId} x1="0" y1="0" x2="0" y2="1">
            <stop stopColor="var(--color-purple-200)" />
            <stop offset="1" stopColor="var(--color-purple-100)" />
          </linearGradient>
        </defs>
      </svg>
      <span className="inline-flex size-full items-center justify-center overflow-visible [&_path]:fill-(--nav-icon-fill) [&_path]:stroke-(--nav-icon-stroke) [&_path]:stroke-[0.7]">
        {children}
      </span>
    </span>
  )
}
